import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { usePageTitle } from "@/components/layout/hooks/usePageTitle";
import { useTeamById, useUpdateTeam } from "./hooks/useTeams";
import { UserSelectionList } from "./components/UserSelectionList";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStaffSearch } from "./hooks/useStaff";
import { Input } from "@/components/ui/input";

const teamSchema = z.object({
  name: z.string().min(1, "Team name is required"),
  userIds: z.array(z.string()).min(1),
  responsibleStaffId: z.string().optional(),
});

type TeamFormData = z.infer<typeof teamSchema>;

export const EditTeamPage: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const updateTeam = useUpdateTeam();
  const { data: teamData, isLoading } = useTeamById(id!);

  // Fetch all staff for the dropdown
  const { data: allStaff } = useStaffSearch({});

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TeamFormData>({
    resolver: zodResolver(teamSchema),
    defaultValues: {
      name: "",
      userIds: [],
    },
  });

  const selectedUserIds = watch("userIds") || [];

  const allSelectedStaff = useMemo(
    () => allStaff?.filter((u) => selectedUserIds.includes(u.id)),
    [allStaff, selectedUserIds],
  );

  // Pre-populate form when data is loaded
  useEffect(() => {
    if (teamData) {
      reset({
        name: teamData.name,
        userIds: teamData.userTeams?.map((ut) => ut.staffUser.profileId) || [],
        responsibleStaffId: teamData.responsibleStaffId || undefined,
      });
    }
  }, [teamData, reset]);

  const onSubmit = async (data: TeamFormData) => {
    try {
      await updateTeam.mutateAsync({
        id: id!,
        data: {
          name: data.name,
          userIds: data.userIds,
          responsibleStaffId: data.responsibleStaffId,
        },
      });
      navigate("/staff/teams");
    } catch (error) {
      console.error("Failed to update team:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  if (!teamData) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div>Team not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          className="gap-2 pl-0 hover:bg-transparent mb-6"
          onClick={() => navigate("/staff/teams")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        <h1 className="text-2xl font-semibold text-center mb-8">Edit team</h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="space-y-2">
            <label
              htmlFor="name"
              className="text-sm font-medium uppercase text-gray-500"
            >
              Team Name
            </label>
            <Input
              id="name"
              {...register("name")}
              placeholder="Name"
              className="h-12 bg-white"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xl font-medium">Users</label>
            <UserSelectionList
              selectedUserIds={selectedUserIds}
              onSelectionChange={(ids) => setValue("userIds", ids)}
              className="h-[500px]"
            />
            {errors.userIds && selectedUserIds.length <= 0 && (
              <p className="text-sm text-red-500">
                You should select at least one user for team
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium uppercase text-gray-500">
              Responsible Staff
            </label>
            <Select
              value={watch("responsibleStaffId") || "none"}
              onValueChange={(value) =>
                setValue(
                  "responsibleStaffId",
                  value === "none" ? undefined : value,
                )
              }
            >
              <SelectTrigger className="h-12 w-full justify-between bg-white">
                <SelectValue placeholder="Select responsible staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {allSelectedStaff?.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              className="w-full max-w-xs bg-black hover:bg-gray-800 text-white h-12 text-base"
              disabled={isSubmitting || updateTeam.isPending}
            >
              {isSubmitting || updateTeam.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
